  import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
// eslint-disable-next-line no-unused-vars
import ImageUploader from '../components/ui/ImageUploader';
import { fetchProducts, addProduct, editProduct } from '../api/products';
import { fetchOrders } from '../api/orders';
import { fetchCustomOrders } from '../api/customOrders';
import { format } from 'date-fns';

// eslint-disable-next-line
const Admin = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('products');
  
  // Products state
  const [productsList, setProductsList] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [ordersPagination, setOrdersPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  // eslint-disable-next-line no-unused-vars
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Custom Orders state
  const [customOrders, setCustomOrders] = useState([]);
  const [customOrdersLoading, setCustomOrdersLoading] = useState(false);
  const [customOrdersError, setCustomOrdersError] = useState(null);
  const [customOrdersPagination, setCustomOrdersPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  // eslint-disable-next-line no-unused-vars
  const [selectedCustomOrder, setSelectedCustomOrder] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [customOrderNotes, setCustomOrderNotes] = useState('');

  // Fetch products when the component mounts
  useEffect(() => {
    if (activeTab === 'products') {
      const getProducts = async () => {
        try {
          setLoading(true);
          const data = await fetchProducts();
          setProductsList(data);
          setLoading(false);
        } catch (err) {
          console.error('Error fetching products:', err);
          setError('Failed to load products. Please try again later.');
          setLoading(false);
        }
      };
      
      getProducts();
    }
  }, [activeTab]);
  
  
  // Fetch orders when the orders tab is active
  useEffect(() => {
    if (activeTab === 'orders') {
      const getOrders = async () => {
        try {
          setOrdersLoading(true);
          const data = await fetchOrders({
            page: ordersPagination.page,
            limit: ordersPagination.limit
          });
          setOrders(data.orders);
          setOrdersPagination({
            page: data.pagination.page,
            limit: data.pagination.limit,
            total: data.pagination.total,
            totalPages: data.pagination.totalPages
          });
          setOrdersLoading(false);
        } catch (err) {
          console.error('Error fetching orders:', err);
          setOrdersError('Failed to load orders. Please try again later.');
          setOrdersLoading(false);
        }
      };
      
      getOrders();
    }
  }, [activeTab, ordersPagination.page, ordersPagination.limit]);
  
  // Fetch custom orders when the custom orders tab is active
  useEffect(() => {
    if (activeTab === 'custom-orders') {
      const getCustomOrders = async () => {
        try {
          setCustomOrdersLoading(true);
          const data = await fetchCustomOrders({
            page: customOrdersPagination.page,
            limit: customOrdersPagination.limit
          });
          setCustomOrders(data.customOrders);
          setCustomOrdersPagination({
            page: data.pagination.page,
            limit: data.pagination.limit,
            total: data.pagination.total,
            totalPages: data.pagination.totalPages
          });
          setCustomOrdersLoading(false);
        } catch (err) {
          console.error('Error fetching custom orders:', err);
          setCustomOrdersError('Failed to load custom orders. Please try again later.');
          setCustomOrdersLoading(false);
        }
      };
      
      getCustomOrders();
    }
  }, [activeTab, customOrdersPagination.page, customOrdersPagination.limit]);
  
  // Form data state
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    price: '',
    description: '',
    material: '',
    color: '',
    crownStyle: '',
    brimSize: '',
    sizes: [{ sizeInCm: '' }],
    images: [''],
    isNew: false,
    isFeatured: false,
    inStock: true
  });

  // Add a new product
  const handleAddNew = () => {
    // Generate a new ID (max ID + 1)
    const newId = productsList.length > 0 
      ? Math.max(...productsList.map(p => p.id)) + 1 
      : 1;
    
    setFormData({
      id: newId,
      name: '',
      price: '',
      description: '',
      material: '',
      color: '',
      crownStyle: '',
      brimSize: '',
      sizes: [{ sizeInCm: '' }],
      images: [''],
      isNew: false,
      isFeatured: false,
      inStock: true
    });
    
    setEditingProduct('new');
  };

  // Start editing a product
  const handleEdit = (product) => {
    setEditingProduct(product.id);
    setFormData({
      ...product,
      sizes: [...(product.sizes || [{ sizeInCm: '' }])],
      images: [...(product.images || [''])]
    });
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle size changes
  const handleSizeChange = (index, value) => {
    const newSizes = [...formData.sizes];
    newSizes[index] = {
      sizeInCm: value
    };
    
    setFormData(prev => ({
      ...prev,
      sizes: newSizes
    }));
  };

  // Handle image URL changes
  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    
    setFormData(prev => ({
      ...prev,
      images: newImages
    }));
  };

  // Add a new size field
  const addSizeField = () => {
    setFormData(prev => ({
      ...prev,
      sizes: [...prev.sizes, { sizeInCm: '' }]
    }));
  };

  // Add a new image field
  const addImageField = () => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, '']
    }));
  };

  // Save changes
  const handleSave = async () => {
    try {
      // Clean up empty sizes and images, convert sizes to correct format
      const cleanedFormData = {
        ...formData,
        price: parseFloat(formData.price),
        sizes: formData.sizes
          .filter(s => s.sizeInCm && s.sizeInCm.toString().trim() !== '')
          .map(s => ({
            sizeInCm: parseInt(s.sizeInCm)
          })),
        images: formData.images.filter(i => i.trim() !== '')
      };
      
      if (editingProduct === 'new') {
        // Add new product to database
        const newProduct = await addProduct(cleanedFormData);
        setProductsList(prev => [...prev, newProduct]);
        alert('Product added successfully!');
      } else {
        // Update existing product in database
        const updatedProduct = await editProduct(editingProduct, cleanedFormData);
        setProductsList(prev => 
          prev.map(p => p.id === editingProduct ? updatedProduct : p)
        );
        alert('Product updated successfully!');
      }
      
      // Reset editing state
      setEditingProduct(null);
      
    } catch (err) {
      console.error('Error saving product:', err);
      alert('Failed to save product. Please try again.');
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setEditingProduct(null);
  };

  // Toggle product status (in stock)
  // eslint-disable-next-line no-unused-vars
  const toggleStatus = async (id) => {
    try {
      const product = productsList.find(p => p.id === id);
      if (!product) return;
      
      const updatedProduct = await editProduct(id, {
        ...product,
        inStock: !product.inStock
      });
      
      setProductsList(prev => 
        prev.map(p => p.id === id ? updatedProduct : p)
      );
      
    } catch (err) {
      console.error('Error updating product status:', err);
      alert('Failed to update product status. Please try again.');
    }
  };

  // Export products data
  const exportProducts = () => {
    const dataStr = `const products = ${JSON.stringify(productsList, null, 2)};\n\nexport default products;`;
    const dataUri = `data:text/javascript;charset=utf-8,${encodeURIComponent(dataStr)}`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', 'products.js');
    linkElement.click();
  };
  
  // Format date
  const formatDate = (dateString) => {
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  };
  
  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-600 text-yellow-100';
      case 'PROCESSING':
        return 'bg-blue-600 text-blue-100';
      case 'SHIPPED':
        return 'bg-purple-600 text-purple-100';
      case 'DELIVERED':
        return 'bg-green-600 text-green-100';
      case 'CANCELLED':
        return 'bg-red-600 text-red-100';
      case 'NEW':
        return 'bg-yellow-600 text-yellow-100';
      case 'CONTACTED':
        return 'bg-blue-600 text-blue-100';
      case 'IN_PROGRESS':
        return 'bg-purple-600 text-purple-100';
      case 'COMPLETED':
        return 'bg-green-600 text-green-100';
      case 'test_payment':
        return 'bg-orange-600 text-orange-100';
      default:
        return 'bg-gray-600 text-gray-100';
    }
  };

  // Handle marking cash payment as paid
  const handleMarkAsPaid = async (orderId) => {
    if (!window.confirm('Подтвердить что клиент оплатил заказ наличными?')) {
      return;
    }

    try {
      console.log('🔄 Marking order as paid:', orderId);
      
      const response = await fetch(`/api/orders/update-payment-status?orderId=${orderId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentStatus: 'SUCCEEDED'
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update payment status');
      }

      const updatedOrder = await response.json();
      console.log('✅ Payment status updated:', updatedOrder);

      // Update the selected order in state
      setSelectedOrder(updatedOrder);
      
      // Update the order in the orders list if it exists
      setOrders(prev => 
        prev.map(order => 
          order.id === orderId 
            ? { ...order, paymentStatus: 'SUCCEEDED', status: 'PROCESSING' }
            : order
        )
      );

      alert('✅ Заказ отмечен как оплаченный! Клиент получит уведомление по email.');

    } catch (error) {
      console.error('❌ Error marking as paid:', error);
      alert(`❌ Ошибка при обновлении статуса: ${error.message}`);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Admin Tabs */}
        <div className="border-b border-gray-800 mb-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => {
                setActiveTab('products');
                setSelectedOrder(null);
                setSelectedCustomOrder(null);
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'products'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Products
            </button>
            <button
              onClick={() => {
                setActiveTab('orders');
                setEditingProduct(null);
                setSelectedCustomOrder(null);
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'orders'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Orders
            </button>
            <button
              onClick={() => {
                setActiveTab('custom-orders');
                setEditingProduct(null);
                setSelectedOrder(null);
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'custom-orders'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Custom Orders
            </button>
          </nav>
        </div>
        
        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h1 className="font-serif text-3xl font-bold text-primary">Product Management</h1>
              <div className="space-x-4">
                <Button onClick={handleAddNew} variant="primary">Add New Product</Button>
                <Button onClick={exportProducts} variant="secondary">Export Products Data</Button>
              </div>
            </div>
            
            {/* Database Integration Notice */}
            <div className="mt-8 bg-secondary border-l-4 border-primary p-4 mb-8">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-primary">Database Integration</h3>
                  <div className="mt-2 text-sm text-gray-400">
                    <p>
                      This admin interface is now connected to the database. Changes you make will be saved to the PostgreSQL database.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Product Form */}
            {editingProduct && (
              <div className="bg-secondary border border-gray-800 rounded-lg p-6 mb-8">
                <h2 className="font-serif text-2xl font-bold text-primary mb-6">
                  {editingProduct === 'new' ? 'Add New Product' : 'Edit Product'}
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Info */}
                  <div>
                    <h3 className="font-serif text-xl font-bold text-light mb-4">Basic Information</h3>
                    
                    <div className="mb-4">
                      <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="name">
                        Product Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full bg-gray-800 border border-gray-700 rounded py-2 px-3 text-light focus:outline-none focus:border-primary"
                        placeholder="Enter product name"
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="price">
                        Price (USD)
                      </label>
                      <input
                        id="price"
                        name="price"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.price}
                        onChange={handleChange}
                        className="w-full bg-gray-800 border border-gray-700 rounded py-2 px-3 text-light focus:outline-none focus:border-primary"
                        placeholder="Enter price"
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="description">
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows="4"
                        className="w-full bg-gray-800 border border-gray-700 rounded py-2 px-3 text-light focus:outline-none focus:border-primary"
                        placeholder="Enter product description"
                      ></textarea>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="material">
                        Material
                      </label>
                      <input
                        id="material"
                        name="material"
                        type="text"
                        value={formData.material}
                        onChange={handleChange}
                        className="w-full bg-gray-800 border border-gray-700 rounded py-2 px-3 text-light focus:outline-none focus:border-primary"
                        placeholder="Enter material"
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="color">
                        Color
                      </label>
                      <input
                        id="color"
                        name="color"
                        type="text"
                        value={formData.color}
                        onChange={handleChange}
                        className="w-full bg-gray-800 border border-gray-700 rounded py-2 px-3 text-light focus:outline-none focus:border-primary"
                        placeholder="Enter color"
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="crownStyle">
                        Crown Style
                      </label>
                      <input
                        id="crownStyle"
                        name="crownStyle"
                        type="text"
                        value={formData.crownStyle}
                        onChange={handleChange}
                        className="w-full bg-gray-800 border border-gray-700 rounded py-2 px-3 text-light focus:outline-none focus:border-primary"
                        placeholder="Enter crown style"
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="brimSize">
                        Brim Size
                      </label>
                      <input
                        id="brimSize"
                        name="brimSize"
                        type="text"
                        value={formData.brimSize}
                        onChange={handleChange}
                        className="w-full bg-gray-800 border border-gray-700 rounded py-2 px-3 text-light focus:outline-none focus:border-primary"
                        placeholder="Enter brim size"
                      />
                    </div>
                  </div>
                  
                  {/* Sizes, Images, and Status */}
                  <div>
                    <h3 className="font-serif text-xl font-bold text-light mb-4">Sizes</h3>
                    
                    {formData.sizes.map((size, index) => (
                      <div key={index} className="mb-4 p-4 border border-gray-700 rounded">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-gray-400 text-sm font-bold" htmlFor={`size-cm-${index}`}>
                            Size (cm)
                          </label>
                          {formData.sizes.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newSizes = [...formData.sizes];
                                newSizes.splice(index, 1);
                                setFormData(prev => ({
                                  ...prev,
                                  sizes: newSizes
                                }));
                              }}
                              className="text-red-400 hover:text-red-300 text-sm"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <input
                          id={`size-cm-${index}`}
                          type="number"
                          min="52"
                          max="64"
                          step="1"
                          value={size.sizeInCm}
                          onChange={(e) => handleSizeChange(index, e.target.value)}
                          className="w-full bg-gray-800 border border-gray-700 rounded py-2 px-3 text-light focus:outline-none focus:border-primary"
                          placeholder="e.g., 58"
                        />
                        <p className="text-xs text-gray-500 mt-1">Valid range: 52-64 cm</p>
                      </div>
                    ))}
                    
                    <div className="mb-6">
                      <Button onClick={addSizeField} variant="secondary" className="w-full">
                        Add Size
                      </Button>
                    </div>
                    
                    <h3 className="font-serif text-xl font-bold text-light mb-4">Images</h3>
                    
                    <div className="mb-4">
                      <label className="block text-gray-400 text-sm font-bold mb-2">
                        Upload Images
                      </label>
                      <ImageUploader
                        multiple={true}
                        maxFiles={5}
                        onUploadComplete={(urls) => {
                          setFormData(prev => ({
                            ...prev,
                            images: [...prev.images.filter(img => img.trim() !== ''), ...urls]
                          }));
                        }}
                        className="mb-4"
                      />
                    </div>

                    {formData.images.map((image, index) => (
                      <div key={index} className="mb-4">
                        <div className="flex items-center">
                          <div className="flex-grow">
                            <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor={`image-${index}`}>
                              Image URL {index + 1}
                            </label>
                            <div className="flex items-center">
                              <input
                                id={`image-${index}`}
                                type="text"
                                value={image}
                                onChange={(e) => handleImageChange(index, e.target.value)}
                                className="w-full bg-gray-800 border border-gray-700 rounded py-2 px-3 text-light focus:outline-none focus:border-primary"
                                placeholder="Enter image URL"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newImages = [...formData.images];
                                  newImages.splice(index, 1);
                                  setFormData(prev => ({
                                    ...prev,
                                    images: newImages
                                  }));
                                }}
                                className="ml-2 p-2 text-red-400 hover:text-red-300"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          {image && (
                            <div className="ml-4">
                              <img 
                                src={image} 
                                alt={`Preview ${index}`} 
                                className="h-16 w-16 object-cover rounded"
                                onError={(e) => {
                                  e.target.src = 'https://placehold.co/100x100/333/666?text=No+Image';
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    <div className="mb-6">
                      <Button onClick={addImageField} variant="secondary" className="w-full">
                        Add Image URL
                      </Button>
                    </div>
                    
                    <h3 className="font-serif text-xl font-bold text-light mb-4">Status</h3>
                    
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="inStock"
                          checked={formData.inStock}
                          onChange={handleChange}
                          className="mr-2"
                        />
                        <span className="text-light">In Stock</span>
                      </label>
                    </div>
                    
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="isNew"
                          checked={formData.isNew}
                          onChange={handleChange}
                          className="mr-2"
                        />
                        <span className="text-light">Mark as New</span>
                      </label>
                    </div>
                    
                    <div className="mb-6">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="isFeatured"
                          checked={formData.isFeatured}
                          onChange={handleChange}
                          className="mr-2"
                        />
                        <span className="text-light">Featured Product</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-4 mt-6">
                  <Button onClick={handleCancel} variant="secondary">
                    Cancel
                  </Button>
                  <Button onClick={handleSave} variant="primary">
                    {editingProduct === 'new' ? 'Add Product' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Products Table (only show if not editing) */}
            {!editingProduct && (
              <>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-light">Loading products...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <p className="text-red-400 mb-4">{error}</p>
                    <Button onClick={() => fetchProducts()} variant="primary">
                      Try Again
                    </Button>
                  </div>
                ) : productsList.length === 0 ? (
                  <div className="bg-secondary border border-gray-800 rounded-lg p-8 text-center">
                    <p className="text-light mb-4">No products found.</p>
                  </div>
                ) : (
                  <div className="bg-secondary border border-gray-800 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-800">
                      <thead className="bg-black">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Image</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Price</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-secondary divide-y divide-gray-800">
                        {productsList.map(product => (
                          <tr key={product.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">#{product.id}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {product.images && product.images.length > 0 ? (
                                <img 
                                  src={product.images[0]} 
                                  alt={product.name} 
                                  className="h-12 w-12 object-cover rounded"
                                  onError={(e) => {
                                    e.target.src = 'https://placehold.co/100x100/333/666?text=No+Image';
                                  }}
                                />
                              ) : (
                                <div className="h-12 w-12 bg-gray-700 rounded flex items-center justify-center">
                                  <span className="text-xs text-gray-400">No image</span>
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-light">{product.name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-primary font-bold">${product.price.toFixed(2)}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs ${product.inStock ? 'bg-green-600 text-green-100' : 'bg-red-600 text-red-100'}`}>
                                {product.inStock ? 'In Stock' : 'Out of Stock'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button 
                                onClick={() => handleEdit(product)} 
                                className="text-primary hover:text-primary/80 mr-3"
                              >
                                Edit
                              </button>
                              <button 
                                onClick={() => window.open(`/products/${product.id}`, '_blank')} 
                                className="text-blue-400 hover:text-blue-300"
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        
        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h1 className="font-serif text-3xl font-bold text-primary">Order Management</h1>
            </div>
            
            {selectedOrder ? (
              <div className="bg-secondary border border-gray-800 rounded-lg p-6 mb-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-serif text-2xl font-bold text-primary">
                    Order Details: {selectedOrder.orderNumber}
                  </h2>
                  <Button onClick={() => setSelectedOrder(null)} variant="secondary">
                    Back to Orders
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-serif text-xl font-bold text-light mb-4">Order Information</h3>
                    
                    <div className="mb-6 bg-gray-800 rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-gray-400 text-sm">Order Date:</p>
                          <p className="text-light">{formatDate(selectedOrder.orderDate)}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Status:</p>
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeClass(selectedOrder.status)}`}>
                            {selectedOrder.status}
                          </span>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Total:</p>
                          <p className="text-primary font-bold">${selectedOrder.total.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Payment Method:</p>
                          <p className="text-light">{selectedOrder.paymentMethod || 'Credit Card'}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Payment Status:</p>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            selectedOrder.paymentStatus === 'SUCCEEDED' ? 'bg-green-600 text-green-100' :
                            selectedOrder.paymentStatus === 'PENDING' ? 'bg-yellow-600 text-yellow-100' :
                            selectedOrder.paymentStatus === 'FAILED' ? 'bg-red-600 text-red-100' :
                            'bg-gray-600 text-gray-100'
                          }`}>
                            {selectedOrder.paymentStatus || 'PENDING'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <h3 className="font-serif text-xl font-bold text-light mb-4">Customer Information</h3>
                    
                    <div className="mb-6 bg-gray-800 rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-gray-400 text-sm">Name:</p>
                          <p className="text-light">{selectedOrder.shippingAddress?.fullName || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Email:</p>
                          <p className="text-light">{selectedOrder.shippingAddress?.email || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Phone:</p>
                          <p className="text-light">{selectedOrder.shippingAddress?.phone || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-serif text-xl font-bold text-light mb-4">Product Information</h3>
                    
                    <div className="mb-6 bg-gray-800 rounded-lg p-4">
                      <div className="flex items-start">
                        {selectedOrder.productImage ? (
                          <img 
                            src={selectedOrder.productImage} 
                            alt={selectedOrder.productName} 
                            className="h-24 w-24 object-cover rounded mr-4"
                            onError={(e) => {
                              e.target.src = 'https://placehold.co/100x100/333/666?text=No+Image';
                            }}
                          />
                        ) : (
                          <div className="h-24 w-24 bg-gray-700 rounded flex items-center justify-center mr-4">
                            <span className="text-xs text-gray-400">No image</span>
                          </div>
                        )}
                        
                        <div>
                          <h4 className="text-light font-bold text-lg">{selectedOrder.productName}</h4>
                          <p className="text-gray-400 text-sm mt-1">Size: {selectedOrder.size || 'Standard'}</p>
                          <p className="text-gray-400 text-sm">Color: {selectedOrder.color || 'As shown'}</p>
                          <p className="text-primary font-bold mt-2">${selectedOrder.price?.toFixed(2) || selectedOrder.total.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <h3 className="font-serif text-xl font-bold text-light mb-4">Shipping Information</h3>
                    
                    <div className="mb-6 bg-gray-800 rounded-lg p-4">
                      <div className="mb-4">
                        <p className="text-gray-400 text-sm">Shipping Address:</p>
                        <p className="text-light">{selectedOrder.shippingAddress?.addressLine1 || '123 Main St'}{selectedOrder.shippingAddress?.addressLine2 ? `, ${selectedOrder.shippingAddress.addressLine2}` : ''}</p>
                        <p className="text-light">{selectedOrder.shippingAddress?.city || 'Anytown'}, {selectedOrder.shippingAddress?.state || 'ST'} {selectedOrder.shippingAddress?.postalCode || '12345'}</p>
                        <p className="text-light">{selectedOrder.shippingAddress?.country || 'United States'}</p>
                      </div>
                      
                      <div>
                        <p className="text-gray-400 text-sm">Shipping Method:</p>
                        <p className="text-light">{selectedOrder.shipping?.option?.service || 'Standard Shipping'}</p>
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-4 mt-6">
                      {selectedOrder.paymentMethod === 'CASH_IN_STORE' && selectedOrder.paymentStatus === 'PENDING' && (
                        <Button 
                          onClick={() => handleMarkAsPaid(selectedOrder.id)}
                          variant="primary"
                          className="bg-green-600 hover:bg-green-700 text-green-100"
                        >
                          💰 Mark as Paid
                        </Button>
                      )}
                      <Button variant="secondary">
                        Update Status
                      </Button>
                      <Button variant="primary">
                        Send Email
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {ordersLoading ? (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-light">Loading orders...</p>
                  </div>
                ) : ordersError ? (
                  <div className="text-center py-12">
                    <p className="text-red-400 mb-4">{ordersError}</p>
                    <Button onClick={() => fetchOrders(1, ordersPagination.limit)} variant="primary">
                      Try Again
                    </Button>
                  </div>
                ) : orders.length === 0 ? (
                  <div className="bg-secondary border border-gray-800 rounded-lg p-8 text-center">
                    <p className="text-light mb-4">No orders found.</p>
                  </div>
                ) : (
                  <div className="bg-secondary border border-gray-800 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-800">
                      <thead className="bg-black">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Order #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Product</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Total</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-secondary divide-y divide-gray-800">
                        {orders.map(order => (
                          <tr key={order.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{order.orderNumber}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{formatDate(order.orderDate)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-light">{order.productName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-primary font-bold">${order.total.toFixed(2)}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeClass(order.status)}`}>
                                {order.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button 
                                onClick={() => {
                                  console.log("Setting selected order:", order);
                                  console.log("Order details:", JSON.stringify(order, null, 2));
                                  setSelectedOrder(order);
                                }} 
                                className="text-primary hover:text-primary/80"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        
        {/* Custom Orders Tab */}
        {activeTab === 'custom-orders' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h1 className="font-serif text-3xl font-bold text-primary">Custom Order Requests</h1>
            </div>
            
            {selectedCustomOrder ? (
              <div className="bg-secondary border border-gray-800 rounded-lg p-6 mb-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-serif text-2xl font-bold text-primary">
                    Custom Order Request Details
                  </h2>
                  <Button onClick={() => setSelectedCustomOrder(null)} variant="secondary">
                    Back to Custom Orders
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-serif text-xl font-bold text-light mb-4">Request Information</h3>
                    
                    <div className="mb-6 bg-gray-800 rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-gray-400 text-sm">Submission Date:</p>
                          <p className="text-light">{formatDate(selectedCustomOrder.submissionDate)}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Status:</p>
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeClass(selectedCustomOrder.status)}`}>
                            {selectedCustomOrder.status}
                          </span>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Budget Range:</p>
                          <p className="text-primary font-bold">${selectedCustomOrder.budgetMin || '100'} - ${selectedCustomOrder.budgetMax || '500'}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Timeline:</p>
                          <p className="text-light">{selectedCustomOrder.timeline || '4-6 weeks'}</p>
                        </div>
                      </div>
                    </div>
                    
                    <h3 className="font-serif text-xl font-bold text-light mb-4">Customer Information</h3>
                    
                    <div className="mb-6 bg-gray-800 rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-gray-400 text-sm">Name:</p>
                          <p className="text-light">{selectedCustomOrder.name}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Email:</p>
                          <p className="text-light">{selectedCustomOrder.email}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Phone:</p>
                          <p className="text-light">{selectedCustomOrder.phone || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-serif text-xl font-bold text-light mb-4">Custom Hat Details</h3>
                    
                    <div className="mb-6 bg-gray-800 rounded-lg p-4">
                      <div className="mb-4">
                        <p className="text-gray-400 text-sm">Hat Type:</p>
                        <p className="text-light font-bold">{selectedCustomOrder.hatType}</p>
                      </div>
                      
                      <div className="mb-4">
                        <p className="text-gray-400 text-sm">Materials:</p>
                        <p className="text-light">{selectedCustomOrder.materials || 'To be discussed'}</p>
                      </div>
                      
                      <div className="mb-4">
                        <p className="text-gray-400 text-sm">Colors:</p>
                        <p className="text-light">{selectedCustomOrder.colors || 'To be discussed'}</p>
                      </div>
                      
                      <div className="mb-4">
                        <p className="text-gray-400 text-sm">Size:</p>
                        <p className="text-light">{selectedCustomOrder.size || 'Standard'}</p>
                      </div>
                      
                      <div>
                        <p className="text-gray-400 text-sm">Description:</p>
                        <p className="text-light">{selectedCustomOrder.description}</p>
                      </div>
                    </div>
                    
                    {selectedCustomOrder.referenceImages && selectedCustomOrder.referenceImages.length > 0 && (
                      <div className="mb-6">
                        <h3 className="font-serif text-xl font-bold text-light mb-4">Reference Images</h3>
                        <div className="grid grid-cols-3 gap-4">
                          {selectedCustomOrder.referenceImages.map((image, index) => (
                            <img 
                              key={index}
                              src={image} 
                              alt={`Reference ${index + 1}`} 
                              className="w-full h-32 object-cover rounded"
                              onError={(e) => {
                                e.target.src = 'https://placehold.co/100x100/333/666?text=No+Image';
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="mb-6">
                      <h3 className="font-serif text-xl font-bold text-light mb-4">Admin Notes</h3>
                      <textarea
                        value={customOrderNotes}
                        onChange={(e) => setCustomOrderNotes(e.target.value)}
                        rows="4"
                        className="w-full bg-gray-800 border border-gray-700 rounded py-2 px-3 text-light focus:outline-none focus:border-primary"
                        placeholder="Add notes about this custom order request..."
                      ></textarea>
                    </div>
                    
                    <div className="flex justify-end space-x-4 mt-6">
                      <Button variant="secondary">
                        Update Status
                      </Button>
                      <Button variant="primary">
                        Contact Customer
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {customOrdersLoading ? (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-light">Loading custom orders...</p>
                  </div>
                ) : customOrdersError ? (
                  <div className="text-center py-12">
                    <p className="text-red-400 mb-4">{customOrdersError}</p>
                    <Button onClick={() => fetchCustomOrders(1, customOrdersPagination.limit)} variant="primary">
                      Try Again
                    </Button>
                  </div>
                ) : customOrders.length === 0 ? (
                  <div className="bg-secondary border border-gray-800 rounded-lg p-8 text-center">
                    <p className="text-light mb-4">No custom order requests found.</p>
                  </div>
                ) : (
                  <div className="bg-secondary border border-gray-800 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-800">
                      <thead className="bg-black">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Hat Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-secondary divide-y divide-gray-800">
                        {customOrders.map(order => (
                          <tr key={order.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{formatDate(order.submissionDate)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-light">{order.name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-light">{order.hatType}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeClass(order.status)}`}>
                                {order.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button 
                                onClick={() => setSelectedCustomOrder(order)} 
                                className="text-primary hover:text-primary/80"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Admin;
